import { useState, useEffect, useRef } from 'react';
import BookingModal from '../../components/feature/BookingModal';
import MobileNav from '../home/components/MobileNav';

export default function HomePage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);

  // Preload hero image
  useEffect(() => {
    const heroImg = new Image();
    heroImg.src = 'https://readdy.ai/api/search-image?query=modern%20elegant%20residential%20interior%20with%20beautiful%20premium%20hardwood%20flooring%20installation%20in%20spacious%20living%20room%2C%20natural%20wood%20grain%20texture%2C%20contemporary%20minimalist%20furniture%2C%20large%20windows%20with%20natural%20lighting%2C%20clean%20simple%20neutral%20background%2C%20professional%20photography%20style%2C%20high-end%20home%20design&width=1920&height=1080&seq=floorvana-hero-bg&orientation=landscape';
    heroImg.onload = () => setHeroImageLoaded(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Update last-modified date in index.html
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Add JSON-LD structured data
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Remove any existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // LocalBusiness Schema
    const localBusinessSchema = document.createElement('script');
    localBusinessSchema.type = 'application/ld+json';
    localBusinessSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Floorvana Flooring",
      "description": "Professional flooring installation services in Ontario, Canada. Specializing in hardwood, laminate, vinyl, vinyl tile, and carpet installation.",
      "url": siteUrl,
      "telephone": "+16476860280",
      "email": "floorvanaflooring@gmail.com",
      "priceRange": "$$",
      "image": `${siteUrl}/vite.svg`,
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "ON",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "addressCountry": "CA"
      },
      "sameAs": [
        "https://www.instagram.com/floorvanaflooring"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "bestRating": "5",
        "worstRating": "1",
        "reviewCount": "5"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "author": { "@type": "Person", "name": "Kaitlin C" },
          "reviewBody": "Matthew and his flooring company did an amazing job from start to finish. He was extremely professional, communicated clearly throughout the process, and made sure everything was done right the first time."
        },
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "author": { "@type": "Person", "name": "Kristen Gamble" },
          "reviewBody": "Amazing work! On time, easy communication and the floors look so good! Couldn't ask for better!"
        },
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "author": { "@type": "Person", "name": "Sarah Laws" },
          "reviewBody": "Matthew is such a hard worker and does an amazing job, very professional. Highly recommend!!"
        },
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "author": { "@type": "Person", "name": "V V" },
          "reviewBody": "Matthew did such a great job with my main floor and basement! The vinyl looks great."
        },
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "author": { "@type": "Person", "name": "Dana Laws" },
          "reviewBody": "Matthew is the best in the business! Highly recommend."
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Flooring Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": "Hardwood Flooring Installation", "description": "Timeless elegance that adds value to your home" }
          },
          {
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": "Laminate Flooring Installation", "description": "Durable, stylish, and easy to maintain" }
          },
          {
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": "Vinyl Flooring Installation", "description": "Water-resistant and perfect for any room" }
          },
          {
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": "Vinyl Tile Installation", "description": "Versatile designs for kitchens and bathrooms" }
          },
          {
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": "Carpet Installation", "description": "Comfort and warmth for cozy living spaces" }
          }
        ]
      }
    });
    document.head.appendChild(localBusinessSchema);

    // WebPage Schema
    const webPageSchema = document.createElement('script');
    webPageSchema.type = 'application/ld+json';
    webPageSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Floorvana Flooring – Professional Flooring Installation Ontario",
      "description": "Transform your space with Floorvana Flooring's expert flooring installation in Ontario. Specializing in hardwood, laminate, vinyl, tile, and carpet. Get your free quote today!",
      "url": siteUrl,
      "inLanguage": "en",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Floorvana Flooring"
      }
    });
    document.head.appendChild(webPageSchema);

    // FAQPage Schema
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What types of flooring does Floorvana Flooring install?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Floorvana Flooring installs hardwood, laminate, vinyl, vinyl tile, and carpet flooring for residential properties in Ontario, Canada."
          }
        },
        {
          "@type": "Question",
          "name": "Does Floorvana Flooring offer free estimates?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Floorvana Flooring offers free, no-obligation estimates. You can book yours by calling +1 (647) 686-0280 or using the online booking form."
          }
        },
        {
          "@type": "Question",
          "name": "How can I contact Floorvana Flooring?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can reach Floorvana Flooring by phone at +1 (647) 686-0280 or by email at floorvanaflooring@gmail.com."
          }
        },
        {
          "@type": "Question",
          "name": "Is vinyl flooring water-resistant?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, vinyl flooring is water-resistant and an excellent choice for kitchens, bathrooms, and basements. Floorvana Flooring specializes in professional vinyl flooring installation."
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    // Intersection Observer for animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => {
      if (observerRef.current) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Clean up script tags
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      name: 'Kaitlin C',
      text: 'Matthew and his flooring company did an amazing job from start to finish. He was extremely professional, communicated clearly throughout the process, and made sure everything was done right the first time. The quality of the work really shows, and it\'s obvious he takes pride in what he does. If you\'re looking for someone reliable who will get the job done properly, I highly recommend Matthew and his team.',
      rating: 5,
    },
    {
      name: 'Kristen Gamble',
      text: 'Amazing work! On time, easy communication and the floors look so good! Couldn\'t ask for better!',
      rating: 5,
    },
    {
      name: 'Sarah Laws',
      text: 'Matthew is such a hard worker and does an amazing job, very professional. Highly recommend!!',
      rating: 5,
    },
    {
      name: 'V V',
      text: 'Matthew did such a great job with my main floor and basement! The vinyl looks great.',
      rating: 5,
    },
    {
      name: 'Dana Laws',
      text: 'Matthew is the best in the business! Highly recommend.',
      rating: 5,
    },
  ];

  const googleReviewsUrl = 'https://www.google.com/search?q=FloorVana+Flooring&stick=H4sIAAAAAAAA_-NgU1I1qLCwMEozMTZPTk02TjK0TDK0MqgwN7M0NDUwNzWzTEsyNDdPXsQq5JaTn18UlpiXqABmZealAwAK4PfwPgAAAA&hl=en#mpd=~4043155896333485884/customers/reviews';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Mobile Navigation */}
        <MobileNav 
          isScrolled={isScrolled}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          onBookingClick={() => setIsBookingModalOpen(true)}
        />

        {/* Hero Section */}
        <header>
          <section 
            className={`relative min-h-screen flex items-center justify-center bg-cover bg-center transition-opacity duration-500 ${heroImageLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=modern%20elegant%20residential%20interior%20with%20beautiful%20premium%20hardwood%20flooring%20installation%20in%20spacious%20living%20room%2C%20natural%20wood%20grain%20texture%2C%20contemporary%20minimalist%20furniture%2C%20large%20windows%20with%20natural%20lighting%2C%20clean%20simple%20neutral%20background%2C%20professional%20photography%20style%2C%20high-end%20home%20design&width=1920&height=1080&seq=floorvana-hero-bg&orientation=landscape)',
              backgroundColor: '#1f2937'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative text-center text-white max-w-4xl px-4 sm:px-6 w-full animate-fade-in-up py-20">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4">Floorvana Flooring</h1>
              <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 font-light px-4">Flooring That Transforms Spaces</p>
              <div className="flex flex-col gap-3 sm:gap-4 px-4 max-w-md mx-auto">
                <a 
                  href="tel:+16476860280" 
                  className="w-full bg-white text-amber-600 hover:bg-gray-100 px-6 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  Call Now
                </a>
                <button 
                  onClick={() => setIsBookingModalOpen(true)}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white px-6 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Book Your Free Estimate
                </button>
              </div>
            </div>
          </section>
        </header>

        <main>
          {/* Services Section */}
          <section id="services" data-animate className="py-12 sm:py-16 md:py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-700 ${visibleSections.has('services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">Our Flooring Services</h2>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">Professional installation for every type of flooring</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8">
                {[
                  { icon: 'ri-home-4-line', title: 'Hardwood', desc: 'Timeless elegance that adds value to your home.' },
                  { icon: 'ri-stack-line', title: 'Laminate', desc: 'Durable, stylish, and easy to maintain.' },
                  { icon: 'ri-drop-line', title: 'Vinyl', desc: 'Water-resistant and perfect for any room.' },
                  { icon: 'ri-grid-line', title: 'Vinyl Tile', desc: 'Versatile designs for kitchens and bathrooms.' },
                  { icon: 'ri-home-heart-line', title: 'Carpet', desc: 'Comfort and warmth for cozy living spaces.' }
                ].map((service, index) => (
                  <article 
                    key={index} 
                    className={`bg-white rounded-lg p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                      visibleSections.has('services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-amber-100 rounded-full mb-4 sm:mb-6 mx-auto transition-transform duration-300 hover:scale-110">
                      <i className={`${service.icon} text-xl sm:text-2xl text-amber-600`}></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 text-center">{service.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">{service.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Before/After Section */}
          <section id="projects" data-animate className="py-12 sm:py-16 md:py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className={`text-center mb-8 sm:mb-12 md:mb-16 transition-all duration-700 ${visibleSections.has('projects') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">See the Difference Floorvana Flooring Delivers</h2>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">Before &amp; after transformations that speak for themselves</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
                {[
                  {
                    title: 'Basement Restoration',
                    before: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/b80573d906e3666b8d35f97d9c508880.jpeg',
                    after: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/5375186b6dd45a637de5f92622e8ebb4.jpeg'
                  },
                  {
                    title: 'Laminate Transformation',
                    before: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/ac699d3bd27c68b95684fbb55bfa63b1.jpeg',
                    after: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/7bff476749c473a4ed6e6d881eee5632.jpeg'
                  },
                  {
                    title: 'Kitchen Vinyl Upgrade',
                    before: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/a4e2c3a489dc0b31fd8a2b203aa671fe.jpeg',
                    after: 'https://static.readdy.ai/image/136a29fb3919f42690833b1cd70c1c44/e5597a8f26fd280a4713dd6431d6fc1d.jpeg'
                  }
                ].map((project, index) => (
                  <article 
                    key={index} 
                    className={`bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                      visibleSections.has('projects') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 text-center">{project.title}</h3>
                      <div className="space-y-4">
                        <div className="transform transition-transform duration-300 hover:scale-105">
                          <p className="text-xs sm:text-sm font-semibold text-gray-500 mb-2">BEFORE</p>
                          <div className="w-full h-48 sm:h-56 overflow-hidden rounded-lg bg-gray-200">
                            <img 
                              src={project.before}
                              alt={`Before ${project.title.toLowerCase()}`}
                              title={`${project.title} - Before renovation`}
                              className="w-full h-full object-cover object-top"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        </div>
                        <div className="flex justify-center">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-600 rounded-full animate-bounce">
                            <i className="ri-arrow-down-line text-white"></i>
                          </div>
                        </div>
                        <div className="transform transition-transform duration-300 hover:scale-105">
                          <p className="text-xs sm:text-sm font-semibold text-gray-500 mb-2">AFTER</p>
                          <div className="w-full h-48 sm:h-56 overflow-hidden rounded-lg bg-gray-200">
                            <img 
                              src={project.after}
                              alt={`After ${project.title.toLowerCase()}`}
                              title={`${project.title} - After renovation`}
                              className="w-full h-full object-cover object-top"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* About Section */}
          <section id="about" data-animate className="py-12 sm:py-16 md:py-20 bg-gray-900 text-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <div className={`transition-all duration-700 ${visibleSections.has('about') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8">About Floorvana Flooring</h2>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed mb-6 sm:mb-8">
                  At <strong>Floorvana Flooring</strong>, we're committed to flawless results. From classic <strong>hardwood flooring</strong> to modern <strong>vinyl flooring</strong>, 
                  every floor we install is built to last. Our experienced team combines traditional 
                  craftsmanship with modern techniques to deliver <strong>flooring solutions</strong> that transform your 
                  space and exceed your expectations.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mt-8 sm:mt-12">
                {[
                  { icon: 'ri-award-line', title: 'Quality Guaranteed', desc: 'Premium materials and expert installation' },
                  { icon: 'ri-time-line', title: 'On-Time Delivery', desc: 'Reliable scheduling and prompt completion' },
                  { icon: 'ri-customer-service-2-line', title: 'Expert Support', desc: 'Professional guidance from start to finish' }
                ].map((feature, index) => (
                  <div 
                    key={index} 
                    className={`text-center transition-all duration-700 ${
                      visibleSections.has('about') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-3 sm:mb-4 transition-transform duration-300 hover:scale-110 hover:rotate-12">
                      <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section id="testimonials" data-animate className="py-12 sm:py-16 md:py-20 bg-white">
            <div className={`max-w-3xl mx-auto px-4 sm:px-6 transition-all duration-700 ${visibleSections.has('testimonials') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="text-center mb-10 sm:mb-14">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">What Our Customers Say</h2>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-amber-500 font-bold text-lg">Google Reviews</span>
                  <span className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                    ))}
                  </span>
                </div>
              </div>

              {/* Carousel */}
              <div className="relative">
                <div className="overflow-hidden rounded-2xl">
                  {testimonials.map((review, index) => (
                    <a
                      key={index}
                      href={googleReviewsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block transition-opacity duration-700 ease-in-out cursor-pointer"
                      style={{
                        display: index === currentTestimonial ? 'block' : 'none',
                        opacity: index === currentTestimonial ? 1 : 0,
                      }}
                    >
                      <div className="bg-gray-50 rounded-2xl px-8 sm:px-12 py-10 sm:py-14 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                        {/* Google G icon */}
                        <div className="flex justify-center mb-5">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100">
                            <span className="font-extrabold text-lg" style={{ color: '#4285F4' }}>G</span>
                          </div>
                        </div>

                        {/* Stars */}
                        <div className="flex justify-center gap-1 mb-6">
                          {[...Array(review.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-amber-400 text-xl sm:text-2xl"></i>
                          ))}
                        </div>

                        {/* Review text */}
                        <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8 italic">
                          &ldquo;{review.text}&rdquo;
                        </p>

                        {/* Reviewer name */}
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 font-bold text-base">
                            {review.name.charAt(0)}
                          </div>
                          <span className="font-semibold text-gray-900 text-base mt-1">{review.name}</span>
                          <span className="text-xs text-gray-400 tracking-wide uppercase">Verified Google Review</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>

                {/* Prev / Next arrows */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow hover:bg-amber-50 hover:border-amber-300 transition-all cursor-pointer z-10"
                  aria-label="Previous review"
                >
                  <i className="ri-arrow-left-s-line text-gray-600 text-xl"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow hover:bg-amber-50 hover:border-amber-300 transition-all cursor-pointer z-10"
                  aria-label="Next review"
                >
                  <i className="ri-arrow-right-s-line text-gray-600 text-xl"></i>
                </button>
              </div>

              {/* Navigation dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${
                      index === currentTestimonial
                        ? 'w-6 h-2.5 bg-amber-500'
                        : 'w-2.5 h-2.5 bg-gray-300 hover:bg-amber-300'
                    }`}
                    aria-label={`Go to review ${index + 1}`}
                  />
                ))}
              </div>

              {/* Read All Reviews Button */}
              <div className="text-center mt-10">
                <a
                  href={googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap shadow-md"
                >
                  <i className="ri-google-fill text-xl"></i>
                  Read All Reviews on Google
                </a>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section id="cta" data-animate className="py-12 sm:py-16 md:py-20 bg-amber-600">
            <div className={`max-w-4xl mx-auto px-4 sm:px-6 text-center transition-all duration-700 ${visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">Ready to Transform Your Space?</h2>
              <p className="text-base sm:text-lg md:text-xl text-amber-100 mb-6 sm:mb-8 leading-relaxed">
                Get your free quote today and discover how Floorvana Flooring can elevate your home
              </p>
              <button 
                onClick={() => setIsBookingModalOpen(true)}
                className="w-full sm:w-auto bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Get Your Free Quote Today
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8 sm:py-10 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Floorvana Flooring</h3>
                <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 leading-relaxed">Transforming spaces with premium flooring solutions</p>
                <div className="space-y-2">
                  <p className="text-sm sm:text-base text-gray-300">
                    <i className="ri-phone-line mr-2"></i>
                    <a href="tel:+16476860280" className="hover:text-white cursor-pointer">+1 (647) 686-0280</a>
                  </p>
                  <p className="text-sm sm:text-base text-gray-300">
                    <i className="ri-mail-line mr-2"></i>
                    <a href="mailto:floorvanaflooring@gmail.com" className="hover:text-white cursor-pointer">floorvanaflooring@gmail.com</a>
                  </p>
                </div>
              </div>
              <nav>
                <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Services</h4>
                <ul className="space-y-2 text-sm sm:text-base text-gray-300">
                  <li><button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Hardwood Flooring</button></li>
                  <li><button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Laminate Installation</button></li>
                  <li><button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Vinyl Flooring</button></li>
                  <li><button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Vinyl Tile Installation</button></li>
                  <li><button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Carpet Installation</button></li>
                </ul>
              </nav>
              <nav>
                <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm sm:text-base text-gray-300">
                  <li><button onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">Gallery</button></li>
                  <li><button onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white cursor-pointer text-left">About Us</button></li>
                  <li><button onClick={() => setIsBookingModalOpen(true)} className="hover:text-white cursor-pointer text-left">Contact</button></li>
                  <li><button onClick={() => setIsBookingModalOpen(true)} className="hover:text-white cursor-pointer text-left">Free Estimate</button></li>
                </ul>
              </nav>
              <div>
                <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Follow Our Work</h4>
                <p className="text-sm sm:text-base text-gray-300 mb-3 sm:mb-4">See our latest projects on Instagram</p>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/floorvanaflooring?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-gray-700 cursor-pointer">
                    <i className="ri-instagram-line text-white"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-400">
              <p>
                © 2024 Floorvana Flooring. All rights reserved. | <a href="https://huntleysolutions.co/" rel="nofollow" className="hover:text-white cursor-pointer">Huntley Solutions </a>
              </p>
            </div>
          </div>
        </footer>
      </div>

      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </>
  );
}
